import { ReactElement } from 'react'
import {
  Button as RACButton,
  ButtonProps as RACButtonProps,
  composeRenderProps
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { Spinner } from '../ui'
import { SpinnerSizes } from '../ui/spinner/constants'
import { pressedRing } from './utils'

export interface ButtonProps extends RACButtonProps {
  /**
   * button type
   */
  hierarchy?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'linkColor'
    | 'linkGray'
    | 'on-brand'
    | 'ai-1'
    | 'ai-2'
    | 'ai-3'
    | 'ghost'
  /**
   * destructive?
   */
  destructive?: boolean
  /**
   * isDisabled?
   */
  isDisabled?: boolean
  /**
   * is loading?
   */
  isLoading?: boolean
  /**
   * How large should the button be?
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  /**
   * Button contents
   */
  label?: string | ReactElement
  /**
   * Button contents
   */
  dotColor?: string
  /**
   * Button contents
   */
  iconLeading?: ReactElement
  /**
   * Button contents
   */
  iconTrailing?: ReactElement
  /**
   * Optional click handler
   */
  onClick?: () => void
}

export const buttonStyles = tv({
  extend: pressedRing,
  base: 'flex items-center text-center gap-2 rounded-sm font-semibold cursor-pointer',
  variants: {
    destructive: {
      false: ''
    },
    isDisabled: {
      false: ''
    },
    size: {
      xsmall: 'text-xs px-2 py-1',
      small: 'text-sm px-3 py-2',
      medium: 'text-base px-4 py-2.5',
      large: 'text-lg px-6 py-3'
    },
    hierarchy: {
      primary: `bg-brand-700 text-neutral-0 
         hover:bg-brand-900 
         active:bg-brand-700`,
      secondary: `bg-neutral-0 text-neutral-700 border-neutral-300 border-solid border outline-none
         hover:bg-neutral-50 hover:text-neutral-800 
         active:bg-neutral-0`,
      tertiary: `bg-neutral-0 text-neutral-700 outline-none
         hover:bg-neutral-50 hover:text-neutral-800 
         active:text-neutral-700 active:bg-neutral-0`,
      linkColor: `p-0 text-highlight-500 outline-none 
         hover:highlight-500 
         active:highlight-500 active:bg-neutral-0`,
      linkGray: `text-neutral-700 hover:text-neutral-900 active:text-neutral-700 active:bg-neutral-0 outline-none`,
      'on-brand':
        'bg-neutral-800 text-neutral-0 hover:bg-brand-900 active:bg-brand-900',
      'ai-1': 'ai-1-mesh text-neutral-0 hover:opacity-90 active:opacity-80',
      'ai-2': 'ai-2-mesh text-neutral-0 hover:opacity-90 active:opacity-80',
      'ai-3': 'ai-3-mesh text-neutral-0 hover:opacity-90 active:opacity-80',
      ghost: `bg-transparent text-inherit hover:bg-neutral-400 hover:bg-opacity-10 active:bg-neutral-200 outline-none`
    },
    grow: {
      true: 'w-full'
    }
  },
  defaultVariants: {
    hierarchy: 'primary'
  },
  compoundVariants: [
    {
      destructive: [true],
      hierarchy: 'primary',
      className: `bg-critical-500 text-neutral-0
         hover:bg-critical-700 
         active:bg-critical-500 `
    },
    {
      destructive: [true],
      hierarchy: 'secondary',
      className: `bg-neutral-0 text-critical-700 border-critical-300 border border-solid outline-none
         hover:bg-critical-50  hover:text-critical-800
         active:text-critical-700 active:bg-neutral-0 active:border-critical-300`
    },
    {
      destructive: [true],
      hierarchy: 'tertiary',
      className: `bg-neutral-0 hover:bg-critical-50 text-critical-700 hover:text-critical-800 active:bg-neutral-0 active:text-critical-700`
    },
    {
      destructive: [true],
      hierarchy: 'linkColor',
      className: `bg-neutral-0 text-critical-700 hover:text-critical-800 active:bg-neutral-0 active:text-critical-700`
    },
    {
      isDisabled: true,
      hierarchy: 'primary',
      className: `bg-neutral-100 hover:bg-neutral-100 active:bg-neutral-100 text-neutral-400 cursor-default`
    },
    {
      isDisabled: true,
      hierarchy: 'secondary',
      className: `bg-neutral-0 hover:bg-neutral-0 hover:text-neutral-400 active:bg-neutral-0 text-neutral-400 cursor-default border-neutral-100 border border-solid`
    },
    {
      isDisabled: true,
      hierarchy: 'tertiary',
      className: `bg-neutral-0 hover:bg-neutral-0 hover:text-neutral-400 active:text-neutral-400 active:bg-neutral-0 text-neutral-400 cursor-default`
    },
    {
      isDisabled: true,
      hierarchy: 'linkColor',
      className: `bg-neutral-0 hover:text-neutral-400 active:text-neutral-400 active:bg-neutral-0 text-neutral-400 cursor-default`
    },
    {
      isDisabled: true,
      hierarchy: 'linkGray',
      className: `bg-neutral-0 hover:text-neutral-400 active:text-neutral-400 active:bg-neutral-0 text-neutral-400 cursor-default`
    },
    {
      isDisabled: true,
      hierarchy: 'ai-1',
      className: `bg-neutral-100 hover:bg-neutral-100 active:bg-neutral-100 text-neutral-400 cursor-default [animation:none] [background:none]`
    },
    {
      isDisabled: true,
      hierarchy: 'ai-2',
      className: `bg-neutral-100 hover:bg-neutral-100 active:bg-neutral-100 text-neutral-400 cursor-default [animation:none] [background:none]`
    },
    {
      isDisabled: true,
      hierarchy: 'ai-3',
      className: `bg-neutral-100 hover:bg-neutral-100 active:bg-neutral-100 text-neutral-400 cursor-default [animation:none] [background:none]`
    }
  ]
})

const AI_HIERARCHIES = ['ai-1', 'ai-2', 'ai-3']

const AiSparklesIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='22'
    height='22'
    viewBox='-4 -4 30 30'
    fill='none'>
    {/* Big star (right) */}
    <path
      className='ai-sparkle-blink-1 ai-sparkle-1'
      d='M14 1L12.8214 4.06442C12.5394 4.79765 12.3984 5.16426 12.1791 5.47264C11.9847 5.74595 11.746 5.98475 11.4726 6.17909C11.1643 6.39836 10.7976 6.53937 10.0644 6.82138L7 8L10.0644 9.17862C10.7976 9.46063 11.1643 9.60164 11.4726 9.82091C11.746 10.0153 11.9847 10.254 12.1791 10.5274C12.3984 10.8357 12.5394 11.2024 12.8214 11.9356L14 15L15.1786 11.9356C15.4606 11.2024 15.6016 10.8357 15.8209 10.5274C16.0153 10.254 16.254 10.0153 16.5274 9.82091C16.8357 9.60164 17.2024 9.46063 17.9356 9.17862L21 8L17.9356 6.82138C17.2024 6.53937 16.8357 6.39836 16.5274 6.17909C16.254 5.98475 16.0153 5.74595 15.8209 5.47264C15.6016 5.16426 15.4606 4.79765 15.1786 4.06442L14 1Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    {/* Medium star (bottom-left) */}
    <path
      className='ai-sparkle-blink-2 ai-sparkle-2'
      d='M5.5 12L4.71554 13.5689C4.45005 14.0999 4.31731 14.3654 4.13997 14.5954C3.98261 14.7996 3.79959 14.9826 3.59545 15.14C3.36538 15.3173 3.0999 15.4501 2.56892 15.7155L1 16.5L2.56892 17.2845C3.0999 17.5499 3.36538 17.6827 3.59545 17.86C3.79959 18.0174 3.98261 18.2004 4.13997 18.4046C4.31731 18.6346 4.45005 18.9001 4.71554 19.4311L5.5 21L6.28446 19.4311C6.54995 18.9001 6.68269 18.6346 6.86003 18.4046C7.01739 18.2004 7.20041 18.0174 7.40455 17.86C7.63462 17.6827 7.9001 17.5499 8.43108 17.2845L10 16.5L8.43108 15.7155C7.9001 15.4501 7.63462 15.3173 7.40455 15.14C7.20041 14.9826 7.01739 14.7996 6.86003 14.5954C6.68269 14.3654 6.54995 14.0999 6.28446 13.5689L5.5 12Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    {/* Small star (top-left cross) */}
    <path
      className='ai-sparkle-blink-2 ai-sparkle-3'
      d='M3.5 6V3.5M3.5 3.5V1M3.5 3.5H1M3.5 3.5H6'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/**
 * Primary UI component for user interaction
 */
export const Button = ({
  hierarchy = 'primary',
  destructive = false,
  size = 'medium',
  label,
  iconLeading,
  iconTrailing,
  isLoading,
  dotColor,
  ...props
}: ButtonProps) => {
  const isAi = AI_HIERARCHIES.includes(hierarchy)
  const resolvedIconLeading =
    iconLeading ?? (isAi ? <AiSparklesIcon /> : undefined)
  return (
    <RACButton
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonStyles({
          ...renderProps,
          hierarchy,
          destructive,
          size,
          className
        })
      )}>
      <div className='flex flex-col w-full'>
        {isLoading && (
          <div className={'w-full h-0 overflow-visible'}>
            <span
              className={`flex items-center justify-center ${
                hierarchy === 'primary' ? 'invert' : ''
              }`}>
              <Spinner size={SpinnerSizes.S} />
            </span>
          </div>
        )}
        <div
          className={`${
            isLoading ? 'invisible' : 'visible'
          } flex flex-row items-center justify-center gap-2 w-full`}>
          {!!resolvedIconLeading && (
            <span className={`shrink-0`}>{resolvedIconLeading}</span>
          )}
          {!!dotColor && (
            <span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='10'
                height='10'
                fill='none'>
                <circle cx='5' cy='5' r='4' fill={dotColor} />
              </svg>
            </span>
          )}
          {!!label && <p className={'truncate no-break ...'}>{label}</p>}
          {!!iconTrailing && <span className={'shrink-0'}>{iconTrailing}</span>}
        </div>
      </div>
    </RACButton>
  )
}
